import { useState } from 'react'
import { BrowserRouter, Routes, Route, Navigate, Outlet } from 'react-router'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { httpBatchLink } from '@trpc/client'
import { Toaster } from 'sonner'
import { trpc } from './lib/trpc'
import { LoginPage } from './pages/auth/login'
import { RegisterPage } from './pages/auth/register'
import { CreateProfilePage } from './pages/profile/create'
import { EditProfilePage } from './pages/profile/edit'
import { BillingPage } from './pages/profile/billing'
import { AdminGuard } from './components/auth/AdminGuard'
import { AdminDashboard } from './pages/admin/AdminDashboard'
import { CVBuilderPage } from './pages/profile/cv-builder'
import { TemplateGalleryPage } from './pages/templates/TemplateGalleryPage'

import { Loader2 } from 'lucide-react'
import { DashboardLayout } from './components/layout/DashboardLayout'
import { ThemeProvider } from './context/ThemeContext'

function AppLoader({ message = "Loading workspace..." }: { message?: string }) {
  return (
    <div className="flex h-screen w-full flex-col items-center justify-center bg-background gap-3 select-none">
      <div className="h-10 w-10 rounded-xl bg-primary text-primary-foreground flex items-center justify-center font-bold text-lg shadow-xs animate-pulse">
        P
      </div>
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <Loader2 className="h-3.5 w-3.5 animate-spin text-primary" />
        <span>{message}</span>
      </div>
    </div>
  );
}

function ProtectedRoute() {
  const { data: session, isLoading } = trpc.auth.me.useQuery()

  if (isLoading) return <AppLoader message="Authenticating session..." />
  
  if (!session) return <Navigate to="/login" replace />
  
  return <Outlet />
}

function DashboardHome() {
  const { data: session, isLoading: sessionLoading } = trpc.auth.me.useQuery()

  if (sessionLoading) return <AppLoader message="Loading dashboard..." />
  if (session?.role === 'admin') return <Navigate to="/admin" replace />

  return <DashboardProfileLoader />
}

function DashboardProfileLoader() {
  const { data: profile, isLoading } = trpc.profile.getMyProfile.useQuery()

  if (isLoading) return <AppLoader message="Loading academic profile..." />

  if (!profile) return <Navigate to="/profile/create" replace />
  
  return <Navigate to="/profile/edit" replace />
}

function App() {
  const [queryClient] = useState(() => new QueryClient())
  const [trpcClient] = useState(() =>
    trpc.createClient({
      links: [
        httpBatchLink({
          url: import.meta.env.VITE_API_URL || 'http://localhost:8787/trpc',
          fetch(url, options) {
            return fetch(url, {
              ...options,
              credentials: 'include',
            });
          },
        }),
      ],
    }),
  )

  return (
    <ThemeProvider>
      <trpc.Provider client={trpcClient} queryClient={queryClient}>
        <QueryClientProvider client={queryClient}>
          <BrowserRouter>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route element={<ProtectedRoute />}>
              <Route path="/" element={<DashboardHome />} />
              <Route path="/profile/create" element={<CreateProfilePage />} />
              
              <Route element={<DashboardLayout />}>
                <Route path="/profile/edit" element={<EditProfilePage />} />
                <Route path="/profile/cv" element={<CVBuilderPage />} />
                <Route path="/profile/billing" element={<BillingPage />} />
                <Route path="/templates" element={<TemplateGalleryPage />} />
                
                <Route element={<AdminGuard />}>
                  <Route path="/admin" element={<AdminDashboard />} />
                </Route>
              </Route>
            </Route>
          </Routes>
        </BrowserRouter>
        <Toaster />
      </QueryClientProvider>
    </trpc.Provider>
  </ThemeProvider>
  )
}

export default App
